import { useEffect, useRef, useState } from 'react'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import {
  formatEuroFr,
  pricesEqual,
  rollingColumns,
} from '../utils/editProductOutcome'
import './RollingPrice.css'

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
const ROLL_MS = 520

function columnsFor(from, to, currentIsTo) {
  return rollingColumns(from, to).map((column) => (
    column.type === 'digit'
      ? { ...column, current: currentIsTo ? column.to : column.from }
      : column
  ))
}

export default function RollingPrice({
  value,
  fromValue = null,
  playKey = null,
  onSettled,
  className = '',
}) {
  const reducedMotion = usePrefersReducedMotion()
  const formatted = formatEuroFr(value)
  const lastKeyRef = useRef(null)
  const settleRef = useRef(onSettled)
  const [columns, setColumns] = useState(() => columnsFor(value, value, true))
  const [instant, setInstant] = useState(true)
  const [fade, setFade] = useState(false)

  settleRef.current = onSettled

  useEffect(() => {
    if (playKey) return undefined
    setInstant(true)
    setColumns(columnsFor(value, value, true))
    return undefined
  }, [formatted, playKey, value])

  useEffect(() => {
    if (!playKey || playKey === lastKeyRef.current) return undefined
    lastKeyRef.current = playKey

    if (fromValue == null || pricesEqual(fromValue, value)) {
      setInstant(true)
      setColumns(columnsFor(value, value, true))
      settleRef.current?.()
      return undefined
    }

    if (reducedMotion) {
      setFade(true)
      setColumns(columnsFor(fromValue, value, false))
      const swap = window.setTimeout(() => {
        setColumns(columnsFor(fromValue, value, true))
        setFade(false)
        settleRef.current?.()
      }, 180)
      return () => window.clearTimeout(swap)
    }

    setInstant(true)
    setColumns(columnsFor(fromValue, value, false))
    let frame2 = 0
    let settleTimer = 0
    const frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        setInstant(false)
        setColumns((prev) => prev.map((column) => (
          column.type === 'digit' ? { ...column, current: column.to } : column
        )))
        settleTimer = window.setTimeout(() => settleRef.current?.(), ROLL_MS)
      })
    })
    return () => {
      window.cancelAnimationFrame(frame1)
      window.cancelAnimationFrame(frame2)
      window.clearTimeout(settleTimer)
    }
  }, [playKey, fromValue, value, reducedMotion])

  return (
    <span
      className={`rolling-price${instant ? ' is-instant' : ''}${reducedMotion ? ' is-fade' : ''}${fade ? ' is-swap' : ''}${className ? ` ${className}` : ''}`}
      aria-label={formatted}
    >
      {columns.map((column, index) => (
        column.type === 'digit' ? (
          <span className="rolling-price-col" key={`d-${index}`}>
            <span
              className="rolling-price-strip"
              style={{ transform: `translateY(-${column.current}em)` }}
            >
              {DIGITS.map((digit) => <span key={digit}>{digit}</span>)}
            </span>
          </span>
        ) : (
          <span className="rolling-price-static" key={`s-${index}`}>
            {column.text}
          </span>
        )
      ))}
    </span>
  )
}
